import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AISimulatorGuide = () => {
  const { t } = useTranslation('help');
  const [activeTab, setActiveTab] = useState('overview');

  const themeStyles = {
    container: {
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
      color: '#ffffff',
      backgroundColor: '#0f172a'
    },
    header: {
      borderBottom: '2px solid #3b82f6',
      paddingBottom: '10px',
      marginBottom: '20px'
    },
    title: {
      margin: '0 0 10px 0',
      color: '#fff'
    },
    subtitle: {
      margin: 0,
      fontStyle: 'italic',
      color: '#94a3b8'
    },
    nav: {
      display: 'flex',
      gap: '5px',
      marginBottom: '20px',
      borderBottom: '1px solid #334155'
    },
    tabButton: (isActive) => ({
      padding: '10px 15px',
      border: '1px solid #334155',
      borderBottom: isActive ? '2px solid #60a5fa' : '1px solid #334155',
      backgroundColor: isActive ? '#1e293b' : '#0f172a',
      color: isActive ? '#ffffff' : '#94a3b8',
      cursor: 'pointer',
      fontWeight: isActive ? 'bold' : 'normal',
      marginBottom: '-1px'
    }),
    heading2: {
      color: '#ffffff',
      borderBottom: '1px solid #1e293b',
      paddingBottom: '6px',
      marginTop: '24px'
    },
    heading3: {
      color: '#cbd5e1',
      marginTop: '16px'
    },
    text: {
      color: '#e2e8f0',
      lineHeight: '1.6'
    },
    list: {
      color: '#e2e8f0',
      lineHeight: '1.6'
    },
    codeBlock: {
      backgroundColor: '#1e293b',
      color: '#38bdf8',
      padding: '12px',
      borderRadius: '6px',
      overflowX: 'auto',
      border: '1px solid #334155'
    },
    inlineCode: {
      backgroundColor: '#1e293b',
      color: '#38bdf8',
      padding: '2px 6px',
      borderRadius: '4px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '20px',
      color: '#e2e8f0'
    },
    tableHeader: {
      border: '1px solid #334155',
      padding: '10px',
      textAlign: 'left',
      backgroundColor: '#1e293b',
      color: '#ffffff'
    },
    tableCell: {
      border: '1px solid #334155',
      padding: '10px'
    }
  };

  return (
    <div style={themeStyles.container}>
      <header style={themeStyles.header}>
        <h2 style={themeStyles.title}>{t('AISimulatorGuide.title')}</h2>
        <p style={themeStyles.subtitle}>
          {t('AISimulatorGuide.subtitle')}
        </p>
      </header>

      {/* Navigation Tabs */}
      <nav style={themeStyles.nav}>
        {[
          { id: 'overview', label: t('AISimulatorGuide.tabs.overview') },
          { id: 'modes', label: t('AISimulatorGuide.tabs.modes') },
          { id: 'endpoints', label: t('AISimulatorGuide.tabs.endpoints') },
          { id: 'config', label: t('AISimulatorGuide.tabs.config') }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={themeStyles.tabButton(activeTab === tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div>
            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.whatThisSimulatorDoes')}</h2>
            <p style={themeStyles.text}>
              {t('AISimulatorGuide.overview.description')}
            </p>
            <ul style={themeStyles.list}>
              <li>{t('AISimulatorGuide.overview.bullet1')}</li>
              <li>{t('AISimulatorGuide.overview.bullet2')}</li>
              <li>{t('AISimulatorGuide.overview.bullet3')}</li>
              <li>{t('AISimulatorGuide.overview.bullet4')}</li>
            </ul>

            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.runtimeRequirements')}</h2>
            <ul style={themeStyles.list}>
              <li>{t('AISimulatorGuide.requirements.bullet1')}</li>
              <li>{t('AISimulatorGuide.requirements.bullet2')}</li>
              <li>{t('AISimulatorGuide.requirements.bullet3')}</li>
              <li>{t('AISimulatorGuide.requirements.bullet4')}</li>
            </ul>

            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.startSimulator')}</h2>
            <p style={themeStyles.text}><strong>{t('AISimulatorGuide.start.basicRun')}</strong></p>
            <pre style={themeStyles.codeBlock}>
              python ai_model_api_simulator.py --host 127.0.0.1 --port 8010 --certfile cert.pem --keyfile key.pem
            </pre>
            <p style={themeStyles.text}><strong>{t('AISimulatorGuide.start.lanRun')}</strong></p>
            <pre style={themeStyles.codeBlock}>
              $env:SIMULATOR_MODE="moderate"$env:SIMULATOR_AUTH_MODE="passthrough"python ai_model_api_simulator.py --host 0.0.0.0 --port 8010 --certfile cert.pem --keyfile key.pem
            </pre>
            <p style={themeStyles.text}><strong>{t('AISimulatorGuide.start.healthCheck')}</strong></p>
            <pre style={themeStyles.codeBlock}>
              curl -k https://127.0.0.1:8010/health
            </pre>
          </div>
        )}

        {activeTab === 'modes' && (
          <div>
            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.modes')}</h2>
            <table style={themeStyles.table}>
              <thead>
                <tr>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.mode')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.behavior')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.typicalUse')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={themeStyles.tableCell}><strong>vulnerable</strong></td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.modes.vulnerable.behavior')}</td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.modes.vulnerable.use')}</td>
                </tr>
                <tr>
                  <td style={themeStyles.tableCell}><strong>moderate</strong></td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.modes.moderate.behavior')}</td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.modes.moderate.use')}</td>
                </tr>
                <tr>
                  <td style={themeStyles.tableCell}><strong>secure</strong></td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.modes.secure.behavior')}</td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.modes.secure.use')}</td>
                </tr>
              </tbody>
            </table>

            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.supportedModels')}</h2>
            <p style={themeStyles.text}>{t('AISimulatorGuide.table.supportedModelsDesc')}</p>
            <table style={themeStyles.table}>
              <thead>
                <tr>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.modelId')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.compatibilityFamily')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.operationFamily')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['gpt-4o-mini', 'OpenAI-compatible', 'chat'],
                  ['gpt-4.1', 'OpenAI-compatible', 'chat'],
                  ['claude-3-5-sonnet-latest', 'Anthropic Claude-compatible', 'messages'],
                  ['gemini-1.5-pro', 'Google Gemini-compatible', 'generateContent'],
                  ['amazon.nova-pro-v1:0', 'AWS Bedrock-compatible', 'converse'],
                  ['mistral-large-latest', 'Mistral-compatible', 'chat'],
                  ['command-r-plus', 'Cohere-compatible', 'chat'],
                  ['llama-3.1-70b-versatile', 'Groq-compatible', 'chat'],
                  ['meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo', 'Together-compatible', 'chat'],
                  ['grok-2', 'xAI-compatible', 'chat'],
                  ['sonar-pro', 'Perplexity-compatible', 'chat']
                ].map(([id, family, op], idx) => (
                  <tr key={idx}>
                    <td style={themeStyles.tableCell}><code style={themeStyles.inlineCode}>{id}</code></td>
                    <td style={themeStyles.tableCell}>{family}</td>
                    <td style={themeStyles.tableCell}>{op}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'endpoints' && (
          <div>
            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.supportedApi')}</h2>
            <table style={themeStyles.table}>
              <thead>
                <tr>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.provider')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.method')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.endpoint')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.simulatorResponse')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['OpenAI-compatible', 'GET', '/v1/models', 'Model discovery list'],
                  ['OpenAI-compatible', 'POST', '/v1/chat/completions', 'Chat Completions-style response'],
                  ['OpenAI-compatible', 'POST', '/v1/responses', 'Responses API-style output'],
                  ['OpenAI-compatible', 'POST', '/v1/embeddings', 'Small deterministic embedding vector'],
                  ['Anthropic Claude', 'POST', '/v1/messages', 'Claude Messages-style content list'],
                  ['Google Gemini', 'POST', '/v1beta/models/{model}:generateContent', 'Gemini candidates'],
                  ['AWS Bedrock', 'POST', '/model/{model_id}/converse', 'Bedrock output.message'],
                  ['Mistral', 'POST', '/mistral/v1/chat/completions', 'OpenAI-like chat envelope'],
                  ['Cohere', 'POST', '/cohere/v2/chat', 'Cohere chat-style response'],
                  ['Simulator', 'GET', '/health', 'Health and active limits']
                ].map(([provider, method, ep, resp], idx) => (
                  <tr key={idx}>
                    <td style={themeStyles.tableCell}>{provider}</td>
                    <td style={themeStyles.tableCell}><code style={themeStyles.inlineCode}>{method}</code></td>
                    <td style={themeStyles.tableCell}><code style={themeStyles.inlineCode}>{ep}</code></td>
                    <td style={themeStyles.tableCell}>{resp}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.integrationExamples')}</h2>
            <h3 style={themeStyles.heading3}>8.1 Python (OpenAI)</h3>
            <pre style={themeStyles.codeBlock}>{`from openai import OpenAI
client = OpenAI(
    api_key="test-key-123",
    base_url="https://127.0.0.1:8010/v1",
)
response = client.chat.completions.create(
    model="gpt-4o-mini",
    messages=[{"role": "user", "content": "Summarize this AI DoS test."}],
    max_tokens=128,
)
print(response.choices[0].message.content)`}</pre>

            <h3 style={themeStyles.heading3}>8.6 JavaScript Fetch</h3>
            <pre style={themeStyles.codeBlock}>{`const response = await fetch("https://127.0.0.1:8010/v1/chat/completions", {
  method: "POST",
  headers: { 
    "Content-Type": "application/json", 
    "Authorization": "Bearer test-key-123" 
  },
  body: JSON.stringify({
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: "Summarize this AI DoS test." }],
    max_tokens=128
  })
});
const data = await response.json();
console.log(data.choices[0].message.content);`}</pre>
          </div>
        )}

        {activeTab === 'config' && (
          <div>
            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.configReference')}</h2>
            <table style={themeStyles.table}>
              <thead>
                <tr>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.envVar')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.default')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.meaning')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['SIMULATOR_MODE', 'moderate', 'vulnerable | moderate | secure behavior mode'],
                  ['SIMULATOR_HOST', '0.0.0.0', 'Default bind host'],
                  ['SIMULATOR_PORT', '8010', 'Default port'],
                  ['SIMULATOR_MAX_BODY_BYTES', '262144', 'Max JSON request body size before 413 rejection'],
                  ['SIMULATOR_MAX_CONCURRENT', '32', 'Max concurrent in-flight POST requests'],
                  ['SIMULATOR_MAX_PROMPT_CHARS', '128000', 'Prompt-size limit outside secure mode'],
                  ['SIMULATOR_AUTH_MODE', 'passthrough', 'off | passthrough | strict authentication settings']
                ].map(([env, def, meaning], idx) => (
                  <tr key={idx}>
                    <td style={themeStyles.tableCell}><code style={themeStyles.inlineCode}>{env}</code></td>
                    <td style={themeStyles.tableCell}><code style={themeStyles.inlineCode}>{def}</code></td>
                    <td style={themeStyles.tableCell}>{meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2 style={themeStyles.heading2}>{t('AISimulatorGuide.sections.troubleshooting')}</h2>
            <table style={themeStyles.table}>
              <thead>
                <tr>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.symptom')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.likelyCause')}</th>
                  <th style={themeStyles.tableHeader}>{t('AISimulatorGuide.table.headers.action')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={themeStyles.tableCell}><strong>{t('AISimulatorGuide.table.troubleshooting.symptom1')}</strong></td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.troubleshooting.cause1')}</td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.troubleshooting.action1')}</td>
                </tr>
                <tr>
                  <td style={themeStyles.tableCell}><strong>{t('AISimulatorGuide.table.troubleshooting.symptom2')}</strong></td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.troubleshooting.cause2')}</td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.troubleshooting.action2')}</td>
                </tr>
                <tr>
                  <td style={themeStyles.tableCell}><strong>{t('AISimulatorGuide.table.troubleshooting.symptom3')}</strong></td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.troubleshooting.cause3')}</td>
                  <td style={themeStyles.tableCell}>{t('AISimulatorGuide.table.troubleshooting.action3')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISimulatorGuide;